import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  output
} from '@angular/core';
import { take } from 'rxjs/operators';
import { FireflyModalService } from '../../modal';
import { ModalRef } from '../../modal/models/base-modal-options';
import { Breakpoint } from '../../utils';
import { FireflyLocalizationService } from '../../utils';
import type { ChartDataEntry, ChartDataSeries, ChartDimensions, ChartLegendOptions } from '../common';
import { FireflyBaseChartComponent } from '../common/base-components/base-chart-component';
import { FireflyChartBoilerplateComponent } from '../common/chart-boilerplate';
import { FireflyLineChartMobilePopoverComponent } from '../common/mobile-popovers/line-chart-popover/line-chart-mobile-popover.component';
import { scaleLinear, ScaleLinear } from 'd3-scale';

@Component({
  selector: 'f-line-chart',
  templateUrl: './line-chart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FireflyLineChartComponent extends FireflyBaseChartComponent implements OnChanges, OnInit {
  @ViewChild(FireflyChartBoilerplateComponent)
  private chartBoilerplate?: FireflyChartBoilerplateComponent;

  @Input() data!: ChartDataSeries[];
  @Input() lineCurved = false;
  @Input() lineWidth = 2;
  @Input() circleRadius = 4;
  /**
   * Default line series palette when no `lineClasses` input is provided.
   * Keeps legend markers and line colors in sync with the Firefly demo palette.
   */
  @Input() lineClasses: string[] = [];
  @Input() showPointerLine = false;
  /** Label for the right Y-axis (dashed-line axis). */
  @Input() lineAxisLabelText!: string;
  /** Tick formatter for the right Y-axis. */
  @Input() lineAxisTickFormatting!: (d: unknown) => string;
  /** Show grid lines on the right (dashed-line) axis. */
  @Input() showDashedSeriesGridlines = false;
  /** Whether the right Y-axis domain should start at zero. */
  @Input() startLineAxisDomainWithZero = true;
  /**
   * Optional separate dataset for dashed-line series rendered on the right Y-axis.
   * Each entry must share the same `name` (x-axis point) as the corresponding
   * entry in `data`. When provided, the right-axis domain is derived exclusively
   * from this dataset, keeping both axes fully independent.
   */
  @Input() dashedSeriesData?: ChartDataSeries[];

  readonly lineClick = output<{ data: ChartDataSeries; index: number }>();

  pointerXCoordinate!: number | undefined;
  activeDataPointId = -1;

  chartLegendOptions!: ChartLegendOptions;

  /** Right-axis scale driven by dashed-line series; undefined when no dashed lines exist. */
  lineYScale?: ScaleLinear<number, number>;

  private computedData: ChartDataSeries[] = [];

  private readonly modalService = inject(FireflyModalService);
  private readonly localizationService = inject(FireflyLocalizationService, { optional: true });
  private mobileModal: ModalRef<unknown> | null = null;
  private modalTitle = 'Chart Details';

  private readonly defaultYAxisTickFormatting = (v: unknown) => `${Number(v)}`;

  ngOnInit(): void {
    this.localizationService
      ?.localize('chartDetailTitle', {})
      .pipe(take(1))
      .subscribe((title: string) => {
        this.modalTitle = title;
      });
  }
  override ngOnChanges(changes?: SimpleChanges): void {
    super.ngOnChanges();
    if (!changes || changes['data'] || changes['dashedSeriesData']) {
      this.computedData = this.buildComputedData();
    }
    if (!changes || changes['lineClasses']) {
      this.warnOnDashedLineInClasses();
    }
    // Populate defaults so legend/color processing works even when inputs are omitted.
    this.ensureDefaultColors();
    if (!this.legendOptions?.data?.length) return;
    if (
      changes &&
      !changes['legendOptions'] &&
      !changes['data'] &&
      !changes['lineClasses'] &&
      !changes['dashedSeriesData']
    )
      return;

    // Use the solid-first sub-series order to index into lineClasses, which is
    // what the line-series renderer also uses (sub-series order in computedData).
    const subSeriesLabels = this.getSubSeriesLabels();
    this.chartLegendOptions = {
      ...this.legendOptions,
      data: this.legendOptions.data.map(item => {
        const seriesIndex = subSeriesLabels.indexOf(item.label);
        const colorClass = this.getLegendColorClass(seriesIndex);
        const markerCssClass = this.getLegendMarkerClass(item.markerCssClass ?? '', colorClass, item.label);
        return markerCssClass === item.markerCssClass ? item : { ...item, markerCssClass };
      })
    };
  }

  get lineSeriesData(): ChartDataSeries[] {
    // Return the pre-merged, stable dataset for the line-series renderer.
    return this.computedData;
  }

  get solidSeriesCount(): number {
    return (this.data?.[0]?.series as ChartDataEntry[])?.length ?? 0;
  }

  get effectiveYAxisTickFormatting(): (d: unknown) => string {
    return this.yAxisTickFormatting ?? this.defaultYAxisTickFormatting;
  }

  /**
   * Merge solid (data) and dashed (dashedSeriesData) sub-series per x-axis point.
   * Called once on data/dashedSeriesData change and stored in computedData.
   */
  private buildComputedData(): ChartDataSeries[] {
    if (!this.dashedSeriesData?.length) return this.data || [];
    const solid = this.data || [];
    const dashedByName = new Map<string, ChartDataEntry[]>(
      this.dashedSeriesData.map(entry => [entry.name as string, (entry.series ?? []) as ChartDataEntry[]])
    );
    return solid.map(entry => ({
      ...entry,
      series: [...(entry.series as ChartDataEntry[]), ...(dashedByName.get(entry.name as string) ?? [])]
    }));
  }

  // ---------------------------------------------------------------------------
  // Dual-scale helpers
  // ---------------------------------------------------------------------------

  /**
   * Returns `dashedSeriesData` when provided; otherwise an empty array.
   * Dashed series are declared exclusively via `dashedSeriesData` — `lineClasses`
   * tokens no longer determine which series are dashed or drive right-axis
   * domain computation.
   */
  private get dashedLineData(): ChartDataSeries[] {
    return this.dashedSeriesData?.length ? this.dashedSeriesData : [];
  }

  /** Build the right-axis scale from dashed-line series values. Returns undefined when no dashed lines exist. */
  getLineYScale(view: ChartDimensions): ScaleLinear<number, number> | undefined {
    const dashedData = this.dashedLineData;
    if (!dashedData.length) return undefined;
    const domain = this.getYDomain(dashedData, this.startLineAxisDomainWithZero);
    return scaleLinear().range([view.height, 0]).domain(domain).nice();
  }

  private alignYScalesToZero(view: ChartDimensions): void {
    if (!this.lineYScale) return;

    const [yMin, yMax] = this.yScale.domain();
    const [lineMin, lineMax] = this.lineYScale.domain();

    // Only align when 0 is reachable in both domains.
    // Use strict inequalities so boundary cases (lineMax === 0 or lineMin === 0) are still aligned.
    if (yMin >= 0 || yMax <= 0 || lineMin > 0 || lineMax < 0) return;

    // Smallest integer k ≥ 1 such that [k·yMin, k·yMax] ⊇ [lineMin, lineMax].
    // lineMin/yMin > 0 (both negative); lineMax/yMax > 0 (both positive).
    const kMin = Math.max(lineMax / yMax, lineMin / yMin);
    const k = Math.ceil(kMin - 1e-10); // subtract epsilon to avoid floating-point bumping an exact integer up by 1

    if (k < 1) return;

    this.lineYScale = scaleLinear()
      .range([view.height, 0])
      .domain([k * yMin, k * yMax]);
  }

  /** Keep all three scales (xScale, yScale, lineYScale) in sync on input changes. */
  protected override update(dims: ChartDimensions): void {
    super.update(dims);
    this.lineYScale = this.getLineYScale(dims);
    this.alignYScalesToZero(dims);
  }

  /** Keep all three scales in sync when the container is resized. */
  override onDimensionsChanged(dims: ChartDimensions): void {
    if (dims.width === this.dimensions?.width && dims.height === this.dimensions?.height) return;

    this.dimensions = dims;
    this.xScale = this.getXScale(dims);
    this.yScale = this.getYScale(dims);
    this.lineYScale = this.getLineYScale(dims);
    this.alignYScalesToZero(dims);
    this.cdr.detectChanges();
  }

  onPointerMove(event: MouseEvent) {
    // Track pointer position and update nearest data point for hover state.
    if (!this.interactions?.hover) return;
    const pointer = this.getPointerDataFromEvent(event);
    if (!pointer) return;

    this.pointerXCoordinate = pointer.x;
    if (pointer.index === this.activeDataPointId) return;
    this.activeDataPointId = pointer.index;
    this.cdr.detectChanges();
  }

  @HostListener('mouseleave') onMouseLeave() {
    // Reset hover state when leaving the chart on non-mobile sizes.
    if (window.innerWidth < Breakpoint.Sm) return;
    this.pointerXCoordinate = undefined;
    this.activeDataPointId = -1;
    this.cdr.detectChanges();
  }

  private getNearestDataPointIndex(x: number): number | null {
    // Find the closest data point index to the pointer x position.
    const names = (this.data || []).map(entry => entry.name);
    let minDiff = Number.MAX_VALUE;
    let nearestIndex = -1;

    names.forEach((name, index) => {
      const scaled = this.xScale(name);
      if (scaled === undefined) return;
      const diff = Math.abs(x - scaled);
      if (diff < minDiff) {
        minDiff = diff;
        nearestIndex = index;
      }
    });

    return nearestIndex >= 0 ? nearestIndex : null;
  }

  protected getYDomain(data: ChartDataSeries[], startFromZero = true): number[] {
    // Compute y-axis domain from all series values, optionally including zero.
    const values = data
      .flatMap(entry =>
        (entry.series as { value?: number | null }[]).map(item => item.value).filter(value => typeof value === 'number')
      )
      .filter(value => typeof value === 'number') as number[];

    const adjustedValues = startFromZero ? [0, ...values] : values;
    if (!adjustedValues.length) return [0, 0];
    const min = Math.min(...adjustedValues);
    const max = Math.max(...adjustedValues);
    return [min, max];
  }

  private getLegendMarkerClass(markerCssClass: string, colorClass: string, label?: string): string {
    let nextClass = markerCssClass ?? '';
    const isDashed = label !== undefined && this.isDashedLabel(label);
    if (isDashed) {
      nextClass = this.appendClass(nextClass, 'dashed-line');
      // Apply the text-color class so the dashed marker border inherits currentColor.
      // Solid markers use bg- classes supplied by the caller and must not be modified.
      if (colorClass) {
        nextClass = this.appendClass(nextClass, colorClass);
      }
    }
    return nextClass;
  }

  /** Returns true when `label` belongs to the dashedSeriesData (dashed) series set. */
  private isDashedLabel(label: string): boolean {
    if (!this.dashedSeriesData?.length) return false;
    const firstEntry = this.dashedSeriesData[0];
    if (!Array.isArray(firstEntry?.series)) return false;
    return (firstEntry.series as ChartDataEntry[]).some(s => s.name === label);
  }

  /**
   * Map line border classes to text classes so legend dashed-line uses currentColor.
   */
  private getLegendColorClass(index: number): string {
    const lineClass = this.lineClasses?.[index] ?? '';
    if (!lineClass) return '';
    const borderToken = lineClass.split(/\s+/).find(cls => cls.startsWith('border-'));
    if (borderToken) {
      return `text-${borderToken.substring('border-'.length)}`;
    }
    return '';
  }

  private warnOnDashedLineInClasses(): void {
    if (typeof ngDevMode !== 'undefined' && !ngDevMode) return;
    const hasDashedClass = (this.lineClasses ?? []).some(c => c.includes('dashed-line'));
    if (hasDashedClass) {
      console.warn(
        'FireflyLineChartComponent: `lineClasses` contains "dashed-line" tokens, which is no longer ' +
          'supported. Use the `dashedSeriesData` input to declare dashed series instead. ' +
          'The "dashed-line" token in `lineClasses` has no effect on right-axis scale computation.'
      );
    }
  }

  private ensureDefaultColors(): void {
    // Apply default line colors and legend entries when caller leaves them unset.
    if (!this.lineClasses || !this.lineClasses.length) {
      this.lineClasses = this.defaultLineClasses;
    } else if (this.dashedSeriesData?.length) {
      // When the user provides lineClasses only for the solid series, auto-extend
      // with matching dashed variants so dashedSeriesData series get the same color palette.
      // Recompute from scratch (idempotent): always derive dashed classes from the
      // first solidCount entries so repeated ngOnChanges calls — or a change to `data`
      // that shifts solidCount — never accumulate stale or duplicate dashed entries.
      const solidCount = (this.data?.[0]?.series as ChartDataEntry[] | undefined)?.length ?? 0;
      const dashedCount = (this.dashedSeriesData[0]?.series as ChartDataEntry[] | undefined)?.length ?? 0;
      const solidClasses = this.lineClasses.slice(0, solidCount);
      const dashedClasses = Array.from({ length: dashedCount }, (_, i) => {
        // Mirror the solid palette — no 'dashed-line' token; visual dashing is
        // determined solely by dashedSeriesData membership.
        return solidClasses[i % Math.max(solidClasses.length, 1)] ?? '';
      });
      this.lineClasses = [...solidClasses, ...dashedClasses];
    }

    if (!this.legendOptions || !this.legendOptions.data?.length) {
      const defaultLegendOptions = this.buildDefaultLegendOptions();
      if (defaultLegendOptions) {
        this.legendOptions = defaultLegendOptions;
      }
    } else if (this.dashedSeriesData?.length) {
      // Caller provided legendOptions for solid series only — auto-append entries
      // for any dashed labels that are absent, using the same color order.
      this.extendLegendOptionsWithDashedEntries();
    }
  }

  /**
   * When the caller provides `legendOptions` covering only solid series, auto-append
   * legend entries for each dashed label (from `dashedSeriesData`) that is missing.
   * Uses the same index-cycling as `ensureDefaultColors` does for `lineClasses`:
   * dashed series i mirrors solid class at index i % solidCount.
   * Idempotent — labels already present in `legendOptions.data` are skipped.
   */
  private extendLegendOptionsWithDashedEntries(): void {
    if (!this.dashedSeriesData?.length || !this.legendOptions?.data) return;
    if (this.legendOptions?.data.length === this.lineSeriesData?.[0].series?.length) return;
    const firstDashed = this.dashedSeriesData[0];
    if (!Array.isArray(firstDashed?.series)) return;
    const dashedLabels = (firstDashed.series as ChartDataEntry[])
      .map(s => s.name)
      .filter((n): n is string => Boolean(n));
    if (!dashedLabels.length) return;
    const existingLabels = new Set(this.legendOptions.data.map(d => d.label));
    const missingLabels = dashedLabels.filter(l => !existingLabels.has(l));
    if (!missingLabels.length) return;
    // lineClasses is already extended at this point: index solidCount+i holds the
    // dashed color that mirrors solid class at i % solidCount.
    const solidCount = (this.data?.[0]?.series as ChartDataEntry[] | undefined)?.length ?? 0;
    const newEntries = missingLabels.map((label, i) => {
      const colorClass = this.getLegendColorClass(solidCount + i);
      const colorPart = colorClass ? ` ${colorClass}` : '';
      return { label, markerCssClass: `legend-line dashed-line${colorPart}` };
    });
    // Interleave dashed entries between solid entries to match the display order used by
    // buildDefaultLegendOptions (getSeriesLabels interleaved order):
    // [solid[0], dashed[0], solid[1], dashed[1], …]
    const solidEntries = this.legendOptions.data;
    const interleaved: typeof solidEntries = [];
    const maxLen = Math.max(solidEntries.length, newEntries.length);
    for (let i = 0; i < maxLen; i++) {
      if (i < solidEntries.length) interleaved.push(solidEntries[i]);
      if (i < newEntries.length) interleaved.push(newEntries[i]);
    }
    this.legendOptions = { ...this.legendOptions, data: interleaved };
  }

  private buildDefaultLegendOptions(): ChartLegendOptions | null {
    // Build legend from series labels so demos still show legend colors without inputs.
    const labels = this.getSeriesLabels();
    if (!labels.length) return null;

    // Track per-group indices so solid and dashed series restart their color
    // cycle independently, keeping Sector N the same color as its solid peer.
    let solidIdx = 0;
    let dashedIdx = 0;

    return {
      data: labels.map(label => {
        const isDashed = this.isDashedLabel(label);
        const colorIndex = isDashed ? dashedIdx++ : solidIdx++;
        return {
          label,
          markerCssClass: `legend-line ${isDashed ? 'dashed-line ' : ''}${this.getDefaultLegendMarkerClass(colorIndex)}`
        };
      }),
      legendContentAlignment: 'center',
      legendPosition: 'bottom',
      legendItemsMarginSize: 3
    };
  }

  /**
   * Returns series labels in INTERLEAVED order (solid[0], dashed[0], solid[1], dashed[1], …).
   * Used for legend display order only — do NOT use where lineClasses index alignment is needed.
   */
  private getSeriesLabels(): string[] {
    const { solidLabels, dashedLabels } = this.splitSeriesLabels();
    const result: string[] = [];
    const maxLen = Math.max(solidLabels.length, dashedLabels.length);
    for (let i = 0; i < maxLen; i++) {
      if (i < solidLabels.length) result.push(solidLabels[i]);
      if (i < dashedLabels.length) result.push(dashedLabels[i]);
    }
    return result;
  }

  /**
   * Returns series labels in SOLID-FIRST order ([...solidLabels, ...dashedLabels]).
   * Must match the sub-series order in computedData and the lineClasses index used
   * by the line-series renderer. Do NOT use for legend display order.
   */
  private getSubSeriesLabels(): string[] {
    const { solidLabels, dashedLabels } = this.splitSeriesLabels();
    return [...solidLabels, ...dashedLabels];
  }

  private splitSeriesLabels(): { solidLabels: string[]; dashedLabels: string[] } {
    const solidLabels: string[] = [];
    const dashedLabels: string[] = [];

    const firstEntry = this.data?.[0];
    if (firstEntry && Array.isArray(firstEntry.series)) {
      solidLabels.push(
        ...(firstEntry.series as { name?: string }[]).map(e => e.name).filter((n): n is string => Boolean(n))
      );
    }

    if (this.dashedSeriesData?.length) {
      const firstLineEntry = this.dashedSeriesData[0];
      if (Array.isArray(firstLineEntry.series)) {
        dashedLabels.push(
          ...(firstLineEntry.series as { name?: string }[]).map(e => e.name).filter((n): n is string => Boolean(n))
        );
      }
    }

    return { solidLabels, dashedLabels };
  }

  private getDefaultLegendMarkerClass(index: number): string {
    const token = this.defaultColorTokens[index % this.defaultColorTokens.length];
    return `bg-${token}`;
  }

  private get defaultLineClasses(): string[] {
    const tokens = this.defaultColorTokens;
    const solidCount = (this.data?.[0]?.series as ChartDataEntry[] | undefined)?.length ?? 0;
    const dashedCount = (this.dashedSeriesData?.[0]?.series as ChartDataEntry[] | undefined)?.length ?? 0;
    // Solid-first order mirrors the sub-series order in computedData so that
    // lineClasses[i] always maps to the correct series in the line-series renderer.
    const solidClasses = Array.from({ length: solidCount }, (_, i) => `border-${tokens[i % tokens.length]}`);
    const dashedClasses = Array.from({ length: dashedCount }, (_, i) => `border-${tokens[i % tokens.length]}`);
    return [...solidClasses, ...dashedClasses];
  }

  private readonly defaultColorTokens = [
    'primary-600',
    'warning-600',
    'accent-2-600',
    'accent-5-500',
    'accent-1-600',
    'accent-6-400'
  ];

  private appendClass(base: string, className: string): string {
    if (!className) return base;
    const tokens = `${base} ${className}`.split(/\s+/).filter(Boolean);
    return [...new Set(tokens)].join(' ');
  }

  /**
   * Opens the mobile popover for the nearest data point on tap.
   *
   * Behavior:
   * - Only runs on mobile and when mobile interactions are allowed.
   * - Reuses the desktop popover template/content.
   * - Creates a modal with a line-only chart (no bars) and the selected point data.
   */
  onPointerClick(event: MouseEvent): void {
    // Ignore clicks on desktop.
    if (window.innerWidth >= Breakpoint.Sm) return;

    // Resolve the nearest data point from the click position.
    const pointer = this.getPointerDataFromEvent(event);
    if (!pointer) return;
    const { index, x } = pointer;

    // Update pointer state so the circle moves to the tapped point.
    this.pointerXCoordinate = x;
    this.activeDataPointId = index;
    this.cdr.detectChanges();

    // Emit so containers (e.g. the mobile popover) can react to the selection.
    const entry = this.data?.[index];
    if (entry) this.lineClick.emit({ data: entry, index });

    // Stop here when already inside a mobile popover to prevent nested modals.
    if (this.interactions?.preventMobile) return;

    // Guard against missing template or an already-open modal.
    if (!this.popoverTemplate || this.mobileModal) return;

    // Build the same popover payload used by the desktop line popover.
    const popoverData = this.buildMobilePopoverData(index);
    const chartBoilerplate = this.chartBoilerplate;
    if (!popoverData || !chartBoilerplate) return;

    const mobileSlice = this.getMobileChartSlice(index);
    // Pass solid-only data to the mobile chart so it can correctly compute
    // solidSeriesCount, lineYScale, and yScale independently, just like the
    // parent chart. Passing computedData (merged) would set solidSeriesCount=14
    // and prevent the right Y-axis from being built.
    const chartData = this.data.slice(mobileSlice.start, mobileSlice.end);
    const dashedSeriesData = this.dashedSeriesData?.slice(mobileSlice.start, mobileSlice.end);

    // Open the mobile modal and inject the line-chart context (no bar data).
    this.mobileModal = this.modalService.open({
      component: FireflyLineChartMobilePopoverComponent,
      modalDialogClass: 'f-chart-mobile-popover',
      context: this.getMobileModalContext(
        popoverData,
        chartData,
        mobileSlice.activeIndex,
        chartBoilerplate,
        dashedSeriesData
      ),
      title: this.modalTitle,
      mobile: true
    });

    // Clear modal ref on close and refresh view for pointer updates.
    this.mobileModal.result.finally(() => (this.mobileModal = null));
    this.cdr.detectChanges();
  }

  private buildMobilePopoverData(index: number): { name: string; series: ChartDataEntry[] } | null {
    // Use computedData so both solid and dashed series appear in the mobile popover.
    const entry = this.computedData[index];
    if (!entry) return null;

    // Use solid-first order so lineClasses indices align with computedData sub-series.
    const seriesLabels = this.getSubSeriesLabels();
    const legendClassMap = this.getLegendClassMap();
    const seriesEntries = (entry.series as ChartDataEntry[]) ?? [];

    // Build a label→entry map, applying the correct lineClass and legend marker class.
    const entryByLabel = new Map<string, ChartDataEntry>();
    seriesLabels.forEach((label, seriesIndex) => {
      const dataEntry = seriesEntries.find(item => item?.name === label);
      if (!dataEntry) return;
      const baseClass = this.lineClasses?.[seriesIndex] ?? '';
      entryByLabel.set(label, {
        ...dataEntry,
        name: label,
        cssClass: this.appendClass(baseClass, legendClassMap[label] ?? '')
      });
    });

    if (!entryByLabel.size) return null;

    // Order rows by legendOptions so the mobile popover matches the legend display order.
    const legendData = (this.chartLegendOptions || this.legendOptions)?.data ?? [];
    const orderedLabels = legendData.length
      ? [
          ...legendData.map(item => item.label).filter(l => entryByLabel.has(l)),
          ...seriesLabels.filter(l => !legendData.some(item => item.label === l))
        ]
      : seriesLabels;

    const series = orderedLabels
      .map(label => entryByLabel.get(label))
      .filter((e): e is ChartDataEntry => e !== undefined);

    return { name: entry.name, series };
  }

  private getMobileModalContext(
    popoverData: { name: string; series: ChartDataEntry[] },
    chartData: ChartDataSeries[],
    activeDataPointId: number,
    chartBoilerplate: FireflyChartBoilerplateComponent,
    dashedSeriesData?: ChartDataSeries[]
  ) {
    return {
      popoverData,
      template: this.popoverTemplate,
      chartData,
      dashedSeriesData,
      activeDataPointId,
      chartBoilerplate,
      component: FireflyLineChartComponent,
      lineClasses: this.lineClasses,
      lineWidth: this.lineWidth,
      circleRadius: this.circleRadius,
      legendOptions: this.chartLegendOptions || this.legendOptions,
      showLegend: this.showLegend
    };
  }

  private getPointerDataFromEvent(event: MouseEvent): { x: number; index: number } | null {
    if (!this.xScale || !this.dimensions) return null;
    const target = event.currentTarget as SVGRectElement | null;
    if (!target) return null;
    const bounds = target.getBoundingClientRect();
    const x = event.clientX - bounds.left;
    const index = this.getNearestDataPointIndex(x);
    if (index === null) return null;
    return { x, index };
  }

  private getLegendClassMap(): Record<string, string> {
    const options = this.chartLegendOptions || this.legendOptions;
    if (!options?.data?.length) return {};
    return options.data.reduce((acc, item) => {
      if (item.label && item.markerCssClass) {
        acc[item.label] = item.markerCssClass;
      }
      return acc;
    }, {} as Record<string, string>);
  }

  private getMobileChartSlice(activeIndex: number): { start: number; end: number; activeIndex: number } {
    const total = this.data?.length ?? 0;
    if (total <= 1) return { start: 0, end: total, activeIndex: 0 };

    const windowSize = Math.min(3, total);
    let start = activeIndex - 1;
    let end = activeIndex + 2;

    if (start < 0) {
      start = 0;
      end = windowSize;
    } else if (end > total) {
      end = total;
      start = Math.max(0, total - windowSize);
    }

    return { start, end, activeIndex: activeIndex - start };
  }
}
